import { useQuery } from "@tanstack/react-query";
import { ApplicationFilterState } from "../components/ApplicationsFilter/ApplicationsFilter";
import { _axios } from "../utils/axios";
import {
  TApplicationField,
  TApplicationFieldOption,
} from "./application-fields.hooks";
import { TDocumentConfig, TForm } from "./forms.hooks";
import { level3ApplicationsKeyStore } from "./level-3-application-approval.hooks";

export type TApplication = {
  id: number;
  userId: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  applicationId: string;
  project_name?: string;
  plot_owner_s_name?: string;
  daysLeftForAction?: number | null;
  isActionAllowed?: boolean;
  level3QueueAt?: number | null;
  level3SubmissionDate?: number | null;
};

export function useLevelThreeApplicationStats({
  filters,
}: {
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: level3ApplicationsKeyStore.stats(filters || {}),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: { status: string; count: number }[];
      }>("/level-3-applications/stats", { filters }),
    staleTime: 10 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useLevelThreeApplicationCounts({
  search,
  filters,
}: {
  search?: string;
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: level3ApplicationsKeyStore.counts(search || "", filters || {}),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: number;
      }>("/level-3-applications/counts", { search, filters }),
    staleTime: 10 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useLevelThreeApplications({
  page,
  limit,
  search,
  sort,
  sortVal,
  filters,
}: {
  page: number;
  limit: number;
  search?: string;
  sort: string;
  sortVal: number;
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: level3ApplicationsKeyStore.list(
      page,
      limit,
      sort,
      sortVal,
      search || "",
      filters || {}
    ),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: any[];
      }>("/level-3-application-approval/list", {
        sort,
        sortVal,
        search,
        page,
        limit,
        filters: filters || {},
      }),
    staleTime: 10 * 60 * 1000,
    select: (d) => d.data,
  });
}

export type TPopulatedForm = TForm & {
  documentConfigs?: TDocumentConfig[];
  fields?: (TApplicationField & {
    options?: TApplicationFieldOption[];
  })[];
};

export function useLevelThreeApplication({ id }: { id: number | string }) {
  return useQuery({
    queryKey: level3ApplicationsKeyStore.id(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TApplication & Record<string, unknown>;
      }>(`/level-3-applications/${id}`),
    select: (d) => d?.data?.data,
    enabled: !!id,
  });
}
